import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useDisclosure } from '@mantine/hooks';
import WebTemplatePicker from '../WebTemplatePicker/WebTemplatePicker';
import useWebGeneration from '../../hooks/useWebGeneration';
import { WebMantineProvider } from '../../WebMantineProvider';
import { fetchWebData } from '../../requests/webProposal';
import { webDataHasPages } from '../../utils/webData';
import { webEditorPath } from '../../utils/webRoutes';

/**
 * Bouton à intégrer dans l'éditeur de proposal existant.
 * - Si le proposal a déjà un site web → "Gérer le site web" → /web-editor/:id
 * - Sinon → "Générer un site web" → modale de sélection du template
 *
 * Props :
 *   proposalId       : ID du proposal
 *   hasWebData       : boolean — indication API / Redux (fallback si GET web_data indisponible)
 *   documentIdentity : { id, type } du document source (opportunité / offre) —
 *                      c'est cette identité, pas l'id de proposal, qui adresse
 *                      la route des variables.
 */
const GenerateWebButtonInner = ({
  proposalId,
  hasWebData: hasWebDataFromProposal,
  documentIdentity = null,
}) => {
  const [pickerOpened, { open: openPicker, close: closePicker }] = useDisclosure(false);
  const {
    generate, loading, error, modal: styleMappingModal,
  } = useWebGeneration();
  /** null = pas encore résolu ; sinon vérité terrain depuis GET /web_data */
  const [derivedHasWeb, setDerivedHasWeb] = useState(null);

  const refetchWebPresence = useCallback(async () => {
    if (!proposalId) {
      setDerivedHasWeb(null);
      return;
    }
    try {
      const data = await fetchWebData(proposalId);
      setDerivedHasWeb(webDataHasPages(data));
    } catch {
      setDerivedHasWeb(null);
    }
  }, [proposalId]);

  useEffect(() => {
    setDerivedHasWeb(null);
    refetchWebPresence();
  }, [refetchWebPresence]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchWebPresence();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refetchWebPresence]);

  const hasWebData = derivedHasWeb !== null
    ? derivedHasWeb
    : !!hasWebDataFromProposal;

  if (hasWebData) {
    return (
      <Button
        leftSection={<FontAwesomeIcon icon={icon({ name: 'pen-to-square', style: 'light' })} style={{ fontSize: 16 }} />}
        variant="light"
        /* L'identité voyage dans l'URL : un site généré avant qu'elle soit
           stockée n'en a pas dans `projectConfig`, et l'éditeur autonome n'a
           aucun autre moyen de la retrouver. */
        onClick={() => {
          window.location.href = webEditorPath(proposalId, documentIdentity);
        }}
      >
        Gérer le site web
      </Button>
    );
  }

  return (
    <>
      {error && (
        <Alert icon={<FontAwesomeIcon icon={icon({ name: 'circle-exclamation', style: 'light' })} style={{ fontSize: 14 }} />} color="red" mb="xs" compact>
          {error}
        </Alert>
      )}
      <Button
        leftSection={<FontAwesomeIcon icon={icon({ name: 'globe', style: 'light' })} style={{ fontSize: 16 }} />}
        onClick={openPicker}
        loading={loading}
      >
        Générer un site web
      </Button>

      <WebTemplatePicker
        opened={pickerOpened}
        onClose={closePicker}
        onGenerate={(templateId) => generate(proposalId, templateId, documentIdentity)}
        documentIdentity={documentIdentity}
        loading={loading}
      />
      {styleMappingModal}
    </>
  );
};

/**
 * Version autonome wrappée dans le MantineProvider (pour injection dans l'éditeur existant).
 */
const GenerateWebButton = (props) => (
  <WebMantineProvider>
    <GenerateWebButtonInner {...props} />
  </WebMantineProvider>
);

export default GenerateWebButton;
